import { FC, useContext, useEffect, useState, type JSX } from "react";
import {
  Button,
  Link,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  tokens,
} from "@fluentui/react-components";
import { Form20Filled, Code20Filled, Delete16Filled } from "@fluentui/react-icons";
import { ElementsPane, ErrorsPane } from "./ElementsPane";
import { openPreviewDialog } from "@src/lib/walter/Preview.tsx";
import { useFields } from "@src/taskpane/hooks/useFields";
import { WalterContext } from "@src/taskpane/providers/walter";
import { Intro } from "@src/taskpane/components/onboarding/Intro";
import useToasts from "@src/taskpane/hooks/useToasts";
import debug from "@src/lib/debug";
import { Toolbar } from "./Toolbar";
import { FocusedElementPane } from "./FocusedElementPane";
import { FieldsPane } from "@src/taskpane/components/FieldsPane/FieldsPane";
import { MigrateStickyFooter } from "@src/taskpane/components/MigrateStickyFooter";
import { useBottomDialog } from "@src/taskpane/hooks/useBottomDialog";
import { ReplacePane } from "@src/taskpane/components/ReplacePane";
import { BottomDialog } from "@src/taskpane/components/home/BottomDialog";
import { ConditionBuilder } from "@src/taskpane/components/ConditionBuilder/ConditionBuilder";
import {
  ConditionCodeGen,
  ConditionGroupData,
  emptyConditionGroup,
} from "@src/taskpane/components/ConditionBuilder/ConditionCodeGen";
import { documentSchemaStore } from "@src/lib/schemas";
import { CodeEditor } from "@src/taskpane/components/CodeEditor/CodeEditor";
import { validate } from "@src/taskpane/components/CodeExpression/validator";
import { Ellipsis } from "lucide-react";
import { ChooseFieldSet } from "@src/taskpane/components/FieldSets/ChooseFieldSet";

interface RenderConditionBottomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const useDialogActionsStyles = makeStyles({
  root: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    rowGap: "12px",
  },
  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 24px",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalXL,
    alignItems: "center",
    minWidth: 0,
  },
  trashButton: {
    minWidth: 0,
    flexShrink: 1,
    padding: 0,
  },
  grow: {
    flexGrow: 1,
  },
});

const RenderConditionBottomDialog: FC<RenderConditionBottomDialogProps> = ({ open, onOpenChange }) => {
  const [condition, setCondition] = useState<ConditionGroupData>(emptyConditionGroup());
  const [code, setCode] = useState<string>(documentSchemaStore.renderCondition?.code ?? "");
  const [selectedTab, setSelectedTab] = useState<"ui" | "code">("ui");

  useEffect(() => {
    setCondition(documentSchemaStore.renderCondition?.ui ?? emptyConditionGroup());
    setCode(documentSchemaStore.renderCondition?.code ?? "");
    setSelectedTab(
      documentSchemaStore.renderCondition?.code && documentSchemaStore.renderCondition.ui === undefined ? "code" : "ui",
    );
  }, []);

  const styles = useDialogActionsStyles();

  if (!open) return null;

  const handleSubmit = async () => {
    onOpenChange(false);
    const updated = selectedTab === "ui" ? { ui: condition, code: ConditionCodeGen.generateCode(condition) } : { code };
    await documentSchemaStore.saveRenderCondition(updated);
  };

  const handleRemove = async () => {
    await documentSchemaStore.saveRenderCondition(undefined);
    setCondition(emptyConditionGroup());
    setCode("");
    onOpenChange(false);
  };

  const actions = (
    <Menu positioning="below-end">
      <MenuTrigger>
        <Button style={{ padding: 0, minWidth: 20 }} appearance="subtle">
          <Ellipsis width="14" height="14" />
        </Button>
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          {selectedTab === "ui" && (
            <MenuItem icon={<Code20Filled width="16" strokeWidth="1.5" />} onClick={() => setSelectedTab("code")}>
              Switch to code
            </MenuItem>
          )}
          {selectedTab === "code" && (
            <MenuItem icon={<Form20Filled width="16" strokeWidth="1.5" />} onClick={() => setSelectedTab("ui")}>
              Switch to UI
            </MenuItem>
          )}
          {documentSchemaStore.renderCondition && (
            <MenuItem icon={<Delete16Filled width="16" strokeWidth="1.5" />} onClick={handleRemove}>
              Delete condition
            </MenuItem>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );

  return (
    <BottomDialog
      title="Document condition"
      actions={actions}
      onOpenChange={({ open }) => {
        if (!open) onOpenChange(false);
      }}
    >
      <div className={styles.root}>
        <Text>Only generate this document if this condition is met.</Text>
        {selectedTab === "ui" && (
          <div style={{ width: "100%" }}>
            <ConditionBuilder
              group={condition}
              nodes={documentSchemaStore.descendants}
              onChange={(conditionGroup) => {
                setCondition(conditionGroup);
                setCode(ConditionCodeGen.generateCode(conditionGroup));
              }}
            />
          </div>
        )}
        {selectedTab === "code" && (
          <div style={{ width: "100%", border: `1px solid ${tokens.colorNeutralStroke1}` }}>
            <CodeEditor
              onChange={({ code }) => {
                if (code !== undefined) setCode(code);
              }}
              code={code}
              validator={(codeType: string) => validate(codeType, ["boolean"])}
            />
          </div>
        )}
        <Button appearance="primary" onClick={handleSubmit}>
          {documentSchemaStore.renderCondition ? "Update condition" : "Save condition"}
        </Button>
      </div>
    </BottomDialog>
  );
};

export const Home: FC<{ setMigrating: (_: boolean) => void }> = ({ setMigrating }) => {
  const { fields } = useFields();
  const {
    isFieldSetSelected,
    setIsFieldSetSelected,
    currentUser,
    replacementsCount,
    replacementsCompleteCount,
    replacementsLoading,
  } = useContext(WalterContext);

  const { showErrors } = useToasts();
  const [dialog, setDialog] = useState<Office.Dialog | null>(null);
  const [showPreviewAction, setShowPreviewAction] = useState(false);
  const { dialog: bottomDialog } = useBottomDialog();
  // State for Render Condition dialog
  const [isRenderConditionOpen, setIsRenderConditionOpen] = useState(false);

  useEffect(() => {
    // If the user does not have a legal entity, they cannot preview a document
    if (currentUser?.recentLegalEntityId) {
      setShowPreviewAction(true);
    }

    if (fields.length > 0) {
      setIsFieldSetSelected(true);
    }
  }, [currentUser, fields]);

  async function previewDocument() {
    try {
      if (dialog) {
        // in the case where the dialog that was previously opened is now closed because it was closed manually
        // we need to catch the error that the .close() call will result in and then set the dialog to null
        try {
          dialog.close();
        } catch (e) {
          debug.info("Error closing dialog", e);
        }
        setDialog(null);
      }

      const result = await openPreviewDialog({ onError: displayErrorToast });

      if (result) {
        setDialog(result.value);
      }
    } catch (e) {
      displayErrorToast(e);
    }
  }

  function displayErrorToast(e: unknown, footerContent?: JSX.Element) {
    let message = "";

    if (!footerContent) {
      footerContent = <Link onClick={previewDocument}>Try again</Link>;
    }

    if (e instanceof Error) {
      message = e.message;
    } else if (typeof e === "string") {
      message = e;
    }

    if (message === "Another preview dialog is open. Please close it and try again.") {
      footerContent = undefined;
    }

    showErrors({
      title: "Error generating preview",
      messages: [message],
      footerContent,
    });
  }

  if (!isFieldSetSelected && fields.length === 0) {
    return (
      <>
        <ChooseFieldSet />
        <Intro />
      </>
    );
  }

  return (
    <>
      <Toolbar
        preview={showPreviewAction ? previewDocument : undefined}
        onOpenRenderConditionDialog={() => setIsRenderConditionOpen(true)}
      />
      <FocusedElementPane />

      <div>
        <ElementsPane />
        <ErrorsPane />
        <FieldsPane />
        <RenderConditionBottomDialog open={isRenderConditionOpen} onOpenChange={setIsRenderConditionOpen} />
        {bottomDialog}
      </div>
      {replacementsLoading && (
        <ReplacePane total={replacementsCount} current={replacementsCompleteCount} loading={replacementsLoading} />
      )}

      <MigrateStickyFooter setMigrating={setMigrating} />
    </>
  );
};
